import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, Grid, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { apiFetch } from '../utils/apiFetch';
import PricingReadiness from '../features/onboarding/PricingReadiness.jsx';

const createDefaults = () => ({
  baseRatePerHour: 120,
  baseRatePerKm: 2,
  baseRatePerMover: 45,
  baseRatePerTruck: 85,
  minimumHours: 2,
  minimumDistance: 0,
  minimumCharge: 250,
  quoteValidityDays: 14,
  flatFees: [
    { name: 'Booking fee', amount: 75, description: 'Standard trip preparation and dispatch fee', enabled: true },
  ],
  surcharges: [
    { name: 'Stairs', type: 'per_floor', amount: 25, conditions: 'When stairs are required', enabled: true },
    { name: 'Long carry', type: 'flat', amount: 40, conditions: 'When parking is far from the entrance', enabled: true },
    { name: 'Packing required', type: 'flat', amount: 120, conditions: 'When customer requests packing help', enabled: true },
  ],
  specialItemFees: [
    { item: 'Piano', amount: 250, description: 'Upright or baby grand piano handling', enabled: true },
    { item: 'Appliance', amount: 80, description: 'Large appliance handling', enabled: true },
    { item: 'Safe', amount: 180, description: 'Heavy safe or oversized item', enabled: true },
  ],
  crewPricing: [
    { crewSize: 2, hourlyRate: 120, minimumHours: 2 },
    { crewSize: 3, hourlyRate: 165, minimumHours: 3 },
  ],
  distanceTiers: [
    { minKm: 0, maxKm: 50, ratePerKm: 2, minimumCharge: 0 },
    { minKm: 51, maxKm: 250, ratePerKm: 2.75, minimumCharge: 350 },
  ],
  timeSurcharges: [
    { dayOfWeek: 'saturday', startTime: '08:00', endTime: '18:00', surchargeType: 'percent', amount: 15, label: 'Saturday premium' },
    { dayOfWeek: 'sunday', startTime: '08:00', endTime: '18:00', surchargeType: 'percent', amount: 20, label: 'Sunday premium' },
  ],
  additionalServices: [
    { name: 'Packing labor', description: 'Packing help requested by customer', price: 120, pricingType: 'flat', enabled: true },
    { name: 'Furniture assembly', description: 'Assembly or disassembly service', price: 75, pricingType: 'flat', enabled: true },
  ],
});

const toNumber = (value) => value === '' || value === null || value === undefined ? undefined : Number(value);

function NumberField({ label, value, onChange, helperText, min = 0 }) {
  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={label}
      value={value ?? ''}
      onChange={event => onChange(toNumber(event.target.value))}
      inputProps={{ min, step: 1 }}
      helperText={helperText}
    />
  );
}

function SectionCard({ title, subtitle, action, children }) {
  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid #D9E2EC', borderRadius: 3, height: '100%', bgcolor: '#fff' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={850} sx={{ color: '#102A43' }}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

export default function MoverPricing({ token }) {
  const [pricing, setPricing] = useState(createDefaults());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [readinessKey, setReadinessKey] = useState(0);

  useEffect(() => {
    const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
    apiFetch('/api/moverPricing/me', { headers })
      .then(async response => {
        if (!response.ok) throw new Error('Unable to load pricing');
        return response.json();
      })
      .then(data => {
        if (data) setPricing({ ...createDefaults(), ...data });
      })
      .catch(() => setError('Unable to load pricing. Defaults are shown below.'))
      .finally(() => setLoading(false));
  }, [token]);

  const update = (key, value) => setPricing(current => ({ ...current, [key]: value }));
  const updateList = (key, index, patch) => setPricing(current => ({
    ...current,
    [key]: current[key].map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
  }));
  const removeListItem = (key, index) => setPricing(current => ({ ...current, [key]: current[key].filter((_, itemIndex) => itemIndex !== index) }));

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
      const response = await apiFetch('/api/moverPricing/me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(pricing),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to save pricing');
      setPricing({ ...createDefaults(), ...data });
      setMessage('Pricing saved. New customer quotes will use these rates.');
      setReadinessKey(key => key + 1);
    } catch (err) {
      setError(err.message || 'Unable to save pricing.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}><Typography>Loading pricing...</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F5F7FA', minHeight: '100%' }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, mb: 3, borderRadius: 3, color: '#fff', background: 'linear-gradient(120deg, #102A43 0%, #1E3A8A 100%)', overflow: 'hidden', position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: 0, opacity: 0.16, backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.18) 25%, transparent 25%), linear-gradient(225deg, rgba(255,255,255,0.14) 25%, transparent 25%)', backgroundSize: '36px 36px' }} />
          <Box sx={{ position: 'relative' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Chip icon={<MonetizationOnIcon />} label="Quote engine input" sx={{ mb: 1.5, bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.22)' }} />
              <Typography variant="h4" fontWeight={900}>Pricing workbook</Typography>
              <Typography sx={{ mt: 0.75, maxWidth: 720, color: 'rgba(255,255,255,0.78)' }}>
                Maintain the rates MOVD uses to estimate jobs. Start with the required base rates, then tune crews, distance, access, special items, and time premiums as your business changes.
              </Typography>
              {pricing.pricingStatus && (
                <Chip
                  label={`Automatic quotes: ${pricing.pricingStatus.status}`}
                  color={pricing.pricingStatus.status === 'COMPLETE' ? 'success' : 'warning'}
                  sx={{ mt: 1.5, fontWeight: 700 }}
                />
              )}
            </Box>
            <Button variant="contained" size="large" startIcon={<SaveIcon />} disabled={saving} onClick={save} sx={{ bgcolor: '#fff', color: '#102A43', '&:hover': { bgcolor: '#E6F0FF' } }}>
              {saving ? 'Saving...' : 'Save pricing'}
            </Button>
          </Stack>
          {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
          {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        </Paper>

        <PricingReadiness token={token} refreshKey={readinessKey} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SectionCard title="Base move pricing" subtitle="The minimum inputs needed for every moving quote.">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><NumberField label="Hourly rate" value={pricing.baseRatePerHour} onChange={value => update('baseRatePerHour', value)} helperText="Core labor rate per hour" /></Grid>
                <Grid item xs={12} sm={6}><NumberField label="Minimum hours" value={pricing.minimumHours} onChange={value => update('minimumHours', value)} /></Grid>
                <Grid item xs={12} sm={6}><NumberField label="Distance rate / km" value={pricing.baseRatePerKm} onChange={value => update('baseRatePerKm', value)} /></Grid>
                <Grid item xs={12} sm={6}><NumberField label="Minimum charge" value={pricing.minimumCharge} onChange={value => update('minimumCharge', value)} /></Grid>
                <Grid item xs={12} sm={6}><NumberField label="Truck rate / hour" value={pricing.baseRatePerTruck} onChange={value => update('baseRatePerTruck', value)} /></Grid>
                <Grid item xs={12} sm={6}><NumberField label="Extra mover rate / hour" value={pricing.baseRatePerMover} onChange={value => update('baseRatePerMover', value)} /></Grid>
                <Grid item xs={12} sm={6}><NumberField label="Quote valid days" value={pricing.quoteValidityDays} onChange={value => update('quoteValidityDays', value)} min={1} /></Grid>
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Flat fees" subtitle="Trip, dispatch, fuel, or booking fees applied once." action={<Button size="small" startIcon={<AddIcon />} onClick={() => update('flatFees', [...(pricing.flatFees || []), { name: '', amount: 0, description: '', enabled: true }])}>Add fee</Button>}>
              <Stack spacing={1.5}>
                {(pricing.flatFees || []).map((fee, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 110px 34px' }, gap: 1 }}>
                    <TextField size="small" label="Fee name" value={fee.name || ''} onChange={event => updateList('flatFees', index, { name: event.target.value })} />
                    <TextField size="small" type="number" label="Amount" value={fee.amount ?? ''} onChange={event => updateList('flatFees', index, { amount: toNumber(event.target.value) })} />
                    <Button color="error" onClick={() => removeListItem('flatFees', index)}><DeleteIcon fontSize="small" /></Button>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Crew packages" subtitle="Use different labor rates by crew size." action={<Button size="small" startIcon={<AddIcon />} onClick={() => update('crewPricing', [...(pricing.crewPricing || []), { crewSize: 2, hourlyRate: 0, minimumHours: pricing.minimumHours || 2 }])}>Add crew</Button>}>
              <Stack spacing={1.5}>
                {(pricing.crewPricing || []).map((crew, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 34px' }, gap: 1 }}>
                    <TextField size="small" type="number" label="Crew size" value={crew.crewSize ?? ''} onChange={event => updateList('crewPricing', index, { crewSize: toNumber(event.target.value) })} />
                    <TextField size="small" type="number" label="Hourly rate" value={crew.hourlyRate ?? ''} onChange={event => updateList('crewPricing', index, { hourlyRate: toNumber(event.target.value) })} />
                    <TextField size="small" type="number" label="Min hours" value={crew.minimumHours ?? ''} onChange={event => updateList('crewPricing', index, { minimumHours: toNumber(event.target.value) })} />
                    <Button color="error" onClick={() => removeListItem('crewPricing', index)}><DeleteIcon fontSize="small" /></Button>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Distance tiers" subtitle="Charge different rates for local and longer-distance moves." action={<Button size="small" startIcon={<AddIcon />} onClick={() => update('distanceTiers', [...(pricing.distanceTiers || []), { minKm: 0, maxKm: 100, ratePerKm: pricing.baseRatePerKm || 0, minimumCharge: 0 }])}>Add tier</Button>}>
              <Stack spacing={1.5}>
                {(pricing.distanceTiers || []).map((tier, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr 34px' }, gap: 1 }}>
                    <TextField size="small" type="number" label="Min km" value={tier.minKm ?? ''} onChange={event => updateList('distanceTiers', index, { minKm: toNumber(event.target.value) })} />
                    <TextField size="small" type="number" label="Max km" value={tier.maxKm ?? ''} onChange={event => updateList('distanceTiers', index, { maxKm: toNumber(event.target.value) })} />
                    <TextField size="small" type="number" label="Rate / km" value={tier.ratePerKm ?? ''} onChange={event => updateList('distanceTiers', index, { ratePerKm: toNumber(event.target.value) })} />
                    <TextField size="small" type="number" label="Min charge" value={tier.minimumCharge ?? ''} onChange={event => updateList('distanceTiers', index, { minimumCharge: toNumber(event.target.value) })} />
                    <Button color="error" onClick={() => removeListItem('distanceTiers', index)}><DeleteIcon fontSize="small" /></Button>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Access and service surcharges" subtitle="Stairs, long carry, packing help, awkward access, or extra handling." action={<Button size="small" startIcon={<AddIcon />} onClick={() => update('surcharges', [...(pricing.surcharges || []), { name: '', type: 'flat', amount: 0, conditions: '', enabled: true }])}>Add surcharge</Button>}>
              <Stack spacing={1.5}>
                {(pricing.surcharges || []).map((fee, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 110px 34px' }, gap: 1 }}>
                    <TextField size="small" label="Surcharge" value={fee.name || ''} onChange={event => updateList('surcharges', index, { name: event.target.value })} />
                    <TextField size="small" type="number" label="Amount" value={fee.amount ?? ''} onChange={event => updateList('surcharges', index, { amount: toNumber(event.target.value) })} />
                    <Button color="error" onClick={() => removeListItem('surcharges', index)}><DeleteIcon fontSize="small" /></Button>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Special item fees" subtitle="Heavy, fragile, or high-effort items." action={<Button size="small" startIcon={<AddIcon />} onClick={() => update('specialItemFees', [...(pricing.specialItemFees || []), { item: '', amount: 0, description: '', enabled: true }])}>Add item</Button>}>
              <Stack spacing={1.5}>
                {(pricing.specialItemFees || []).map((fee, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 110px 34px' }, gap: 1 }}>
                    <TextField size="small" label="Item" value={fee.item || ''} onChange={event => updateList('specialItemFees', index, { item: event.target.value })} />
                    <TextField size="small" type="number" label="Amount" value={fee.amount ?? ''} onChange={event => updateList('specialItemFees', index, { amount: toNumber(event.target.value) })} />
                    <Button color="error" onClick={() => removeListItem('specialItemFees', index)}><DeleteIcon fontSize="small" /></Button>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard title="Time premiums" subtitle="Weekend, evening, or other schedule-based adjustments." action={<Button size="small" startIcon={<AddIcon />} onClick={() => update('timeSurcharges', [...(pricing.timeSurcharges || []), { dayOfWeek: 'saturday', startTime: '08:00', endTime: '18:00', surchargeType: 'percent', amount: 0, label: '' }])}>Add premium</Button>}>
              <Stack spacing={1.5}>
                {(pricing.timeSurcharges || []).map((premium, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 92px 92px 100px 34px' }, gap: 1 }}>
                    <TextField select size="small" label="Day" value={premium.dayOfWeek || 'saturday'} onChange={event => updateList('timeSurcharges', index, { dayOfWeek: event.target.value })}>
                      {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => <MenuItem key={day} value={day}>{day[0].toUpperCase() + day.slice(1)}</MenuItem>)}
                    </TextField>
                    <TextField size="small" type="time" label="Start" value={premium.startTime || '08:00'} InputLabelProps={{ shrink: true }} onChange={event => updateList('timeSurcharges', index, { startTime: event.target.value })} />
                    <TextField size="small" type="time" label="End" value={premium.endTime || '18:00'} InputLabelProps={{ shrink: true }} onChange={event => updateList('timeSurcharges', index, { endTime: event.target.value })} />
                    <TextField size="small" type="number" label="Amount" value={premium.amount ?? ''} onChange={event => updateList('timeSurcharges', index, { amount: toNumber(event.target.value) })} />
                    <Button color="error" onClick={() => removeListItem('timeSurcharges', index)}><DeleteIcon fontSize="small" /></Button>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          These rates are used for customer quote estimates. Insurance, third-party protection, and cancellation policies are handled by MOVD at the platform level.
        </Alert>
      </Box>
    </Box>
  );
}
